<template>
  <div>
    <!-- Filter checkboxes -->
    <div class="title_check_2">
      <label v-for="option in filterOptions" :key="option.value" class="filter-option">
        <input type="checkbox" :value="option.value" v-model="filters" />
        <span class="radio-span-2">{{ option.label }}</span>
      </label>
    </div>

    <!-- Map Container -->
    <div id="map"></div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet.markercluster";
import { mapGetters } from "vuex";

const labelMap = new Map([
  ["musty_damp_odor", "Musty Damp Odor"],
  ["visible_mold", "Visible Mold"],
  ["symptoms", "Symptoms"]
]);

export default {
  data() {
    return {
      map: null,
      filters: ["musty_damp_odor", "visible_mold", "symptoms"],
      markerClusterGroup: null,
      filterOptions: [
        { value: "musty_damp_odor", label: "Musty Damp Odor (●)" },
        { value: "visible_mold", label: "Visible Mold (■)" },
        { value: "symptoms", label: "Symptoms (▲)" },
      ],
    };
  },
  computed: {
    ...mapGetters(["getAnalyticsAll"]),
  },
  watch: {
    getAnalyticsAll: {
      handler() { this.updateMarkers(); },
      deep: true,
    },
    filters() { this.updateMarkers(); },
  },
  mounted() {
    this.initMap();
    
    this.markerClusterGroup = L.markerClusterGroup({
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      iconCreateFunction: (cluster) => {
        const children = cluster.getAllChildMarkers(); const count = cluster.getChildCount();
        let totalRedMarkers = 0, totalCircleCount = 0, totalSquareCount = 0, totalTriangleCount = 0;
        children.forEach(marker => {
          if (!marker.options) return;
          if (marker.options.markerColor === 'red') totalRedMarkers++;
          const shape = marker.options.markerShape;
          if (shape === 'circle') totalCircleCount++; else if (shape === 'square') totalSquareCount++; else if (shape === 'triangle') totalTriangleCount++;
        });
        let mainTextFontSize, baseIconDiameter;
        if (count < 10) { baseIconDiameter = 30; mainTextFontSize = 13; } else if (count < 100) { baseIconDiameter = 36; mainTextFontSize = 15; } else if (count < 1000) { baseIconDiameter = 44; mainTextFontSize = 14; } else { baseIconDiameter = 52; mainTextFontSize = 13; }
        let iconDiameter = Math.round(Math.max(baseIconDiameter, (String(count).length * mainTextFontSize * 0.65) + 10, 20));
        iconDiameter = Math.min(iconDiameter, 65);
        const radius = (iconDiameter / 2) - 1.5; const centerX = iconDiameter / 2; const centerY = iconDiameter / 2;
        const redPercent = count > 0 ? (totalRedMarkers / count) * 100 : 0; const redColor = '#e74c3c'; const greenColor = '#2ecc71';
        let piePaths = '';
        if (redPercent >= 100) { piePaths = `<circle cx="${centerX}" cy="${centerY}" r="${radius}" fill="${redColor}"/>`; } else if (redPercent <= 0) { piePaths = `<circle cx="${centerX}" cy="${centerY}" r="${radius}" fill="${greenColor}"/>`; } else { const splitAngle = 3.6 * redPercent; const redPath = this.describeSector(centerX, centerY, radius, 0, splitAngle); const greenPath = this.describeSector(centerX, centerY, radius, splitAngle, 360); piePaths = `<path d="${greenPath}" fill="${greenColor}" /><path d="${redPath}" fill="${redColor}" />`; }
        const innerRadius = radius * 0.58; 
        const svgHtml = `<svg width="${iconDiameter}" height="${iconDiameter}" viewBox="0 0 ${iconDiameter} ${iconDiameter}" xmlns="http://www.w3.org/2000/svg"><g>${piePaths}<circle cx="${centerX}" cy="${centerY}" r="${innerRadius}" fill="white" stroke="rgba(0,0,0,0.15)"/><text x="${centerX}" y="${centerY}" font-family="-apple-system,sans-serif" font-size="${mainTextFontSize}px" font-weight="bold" fill="#333" text-anchor="middle" dominant-baseline="central">${count}</text></g></svg>`;
        const shapeLegend = { circle: '● Musty', square: '■ Mold', triangle: '▲ Symptoms' };
        let tooltipParts = [`<strong>Total Reports: ${count}</strong>`];
        let shapeDetails = [];
        if (totalCircleCount > 0) shapeDetails.push(`${shapeLegend.circle}: ${totalCircleCount}`);
        if (totalSquareCount > 0) shapeDetails.push(`${shapeLegend.square}: ${totalSquareCount}`);
        if (totalTriangleCount > 0) shapeDetails.push(`${shapeLegend.triangle}: ${totalTriangleCount}`);
        if (shapeDetails.length > 0) tooltipParts.push('<hr style="margin:4px 0;border-top:1px solid #ddd;">', ...shapeDetails);
        const totalGreenMarkers = count - totalRedMarkers;
        if (totalRedMarkers > 0 || totalGreenMarkers > 0) { tooltipParts.push('<hr style="margin:4px 0;border-top:1px solid #ddd;">'); tooltipParts.push(`<span style="color:${redColor};font-weight:bold;">Problem (Red): ${totalRedMarkers}</span>`); tooltipParts.push(`<span style="color:${greenColor};font-weight:bold;">No Problem (Green): ${totalGreenMarkers}</span>`); }
        cluster.bindTooltip(`<div style="font-size:12.5px;line-height:1.7;">${tooltipParts.join('<br>')}</div>`);
        return L.divIcon({ html: svgHtml, className: 'marker-cluster', iconSize: [iconDiameter, iconDiameter] });
      }
    });

    this.map.addLayer(this.markerClusterGroup);
    this.addLegend();
    this.updateMarkers();
  },
  methods: {
    polarToCartesian(centerX, centerY, radius, angleInDegrees) { const a = (angleInDegrees-90)*Math.PI/180; return { x:centerX+(radius*Math.cos(a)), y:centerY+(radius*Math.sin(a)) }; },
    describeSector(x, y, radius, startAngle, endAngle) { if(endAngle-startAngle>=360){endAngle=359.99} const s=this.polarToCartesian(x,y,radius,startAngle); const e=this.polarToCartesian(x,y,radius,endAngle); const l=endAngle-startAngle<=180?"0":"1"; return['M',x,y,'L',s.x,s.y,'A',radius,radius,0,l,1,e.x,e.y,'Z'].join(' ') },
    
    initMap() {
      this.map = L.map("map").setView([39.8283, -98.5795], 5);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
      }).addTo(this.map);
    },

    // MODIFIED: This method now creates and adds a legend for each position specified.
    addLegend() {
      const positions = ['topright', 'bottomright']; // Define all desired positions

      positions.forEach(position => {
        const legend = L.control({ position: position }); // Use the current position from the loop
        
        legend.onAdd = () => {
          const div = L.DomUtil.create('div', 'legend');
          L.DomEvent.disableClickPropagation(div).disableScrollPropagation(div);
          const redColor = '#e74c3c', greenColor = '#2ecc71';
          const clusterSvg = `<svg width="40" height="40" viewBox="0 0 40 40"><path d="${this.describeSector(20,20,18.5,144,360)}" fill="${greenColor}"/><path d="${this.describeSector(20,20,18.5,0,144)}" fill="${redColor}"/><circle cx="20" cy="20" r="10.73" fill="white" stroke="rgba(0,0,0,0.15)"/><text x="20" y="20" font-family="-apple-system,sans-serif" font-size="15px" font-weight="bold" fill="#333" text-anchor="middle" dominant-baseline="central">XX</text></svg>`;
          div.innerHTML = `
            <div class="legend-header">
              <h4>Legend</h4>
              <button type="button" class="legend-toggle" aria-label="Toggle legend" title="Toggle legend">
                <span class="toggle-icon"></span>
              </button>
            </div>
            <div class="legend-content">
              <div class="legend-section"><strong>Marker Shapes</strong><div class="legend-item"><div class="legend-shape circle"></div><span>Musty Odor</span></div><div class="legend-item"><div class="legend-shape square"></div><span>Visible Mold</span></div><div class="legend-item"><div class="legend-shape triangle"></div><span>Symptoms</span></div></div>
              <div class="legend-section"><strong>Marker Colors</strong><div class="legend-item"><div class="legend-color-box" style="background:${redColor}"></div><span>Problem</span></div><div class="legend-item"><div class="legend-color-box" style="background:${greenColor}"></div><span>No Problem</span></div></div>
              <div class="legend-section"><strong>Clusters</strong><div class="legend-cluster-example">${clusterSvg}<div class="legend-cluster-desc"><strong>Number:</strong> Total reports<br><strong>Ring:</strong> Problem proportion</div></div></div>
            </div>
          `;

          div.classList.add('minimized');
          const toggleButton = div.querySelector('.legend-toggle');
          toggleButton.querySelector('.toggle-icon').textContent = '+';

          toggleButton.addEventListener('click', (e) => {
            const legendEl = e.currentTarget.closest('.legend');
            legendEl.classList.toggle('minimized');
            const icon = e.currentTarget.querySelector('.toggle-icon');
            icon.textContent = legendEl.classList.contains('minimized') ? '+' : '–';
          });
          return div;
        };
        legend.addTo(this.map);
      });
    },
    
    updateMarkers() {
      this.markerClusterGroup.clearLayers();
      const rows = Object.values(this.getAnalyticsAll || {});
      if (!rows.length) return;
      const markersToAdd = [];
      for (const item of rows) {
        if (!item.coordinates || !item.coordinates.latitude || !item.coordinates.longitude) continue;
        const lat = parseFloat(item.coordinates.latitude); const lng = parseFloat(item.coordinates.longitude);
        for (const field of this.filters) {
          const value = item[field];
          if (value !== true && value !== false) continue;
          const shapeClass = { musty_damp_odor: "circle", visible_mold: "square", symptoms: "triangle" }[field];
          const color = value ? "red" : "green";
          const marker = L.marker([lat, lng], {
            icon: L.divIcon({ html: `<div class="custom-marker ${shapeClass} ${color}"></div>`, className: '', iconSize: [18, 18], iconAnchor: [9, 9] }),
            markerColor: color, markerShape: shapeClass
          }).bindTooltip(`<strong>${this.formatLabel(field)}:</strong> ${value ? "Yes" : "No"}<br/><strong>Kit ID:</strong> ${item.kit_id || "N/A"}`);
          markersToAdd.push(marker);
        }
      }
      this.markerClusterGroup.addLayers(markersToAdd);
    },

    formatLabel(field) {
      return labelMap.get(field) || field;
    },
  },
};
</script>


<style >
body { 
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"; 
}

#map {
  height: 85vh;
  width: 100%;
  z-index: 0;
}

.title_check_2 { margin: 10px 0; font-size: 15px; color: #333; display: flex; gap: 20px; flex-wrap: wrap; align-items: center; padding-left: 10px; }
.radio-span-2 { margin-left: 5px; }
.marker-cluster { background-color: transparent !important; border: none !important; filter: drop-shadow(0px 1px 2px rgba(0,0,0,0.25)) drop-shadow(0px 2px 4px rgba(0,0,0,0.15)); transition: transform 0.15s cubic-bezier(0.25, 0.1, 0.25, 1); }
.marker-cluster:hover { transform: scale(1.08); }
.marker-cluster div { display: none !important; }

/* Corrected Marker Styles */
.custom-marker { 
  width: 18px; height: 18px; display: inline-block; box-sizing: border-box;
}
.custom-marker.circle,
.custom-marker.square {
  border: 2px solid rgba(0,0,0,0.4);
  box-shadow: 0 1px 3px rgba(0,0,0,0.4);
}
.custom-marker.circle { 
  border-radius: 50%; 
}
.custom-marker.triangle { 
  width: 0; height: 0; background: none !important;
  border: none; box-shadow: none;
  border-left: 9px solid transparent; 
  border-right: 9px solid transparent; 
  border-bottom: 18px solid currentColor; 
  filter: drop-shadow(0px 1px 1px rgba(0,0,0,0.4));
}
.custom-marker.green { 
  background-color: #2ecc71; 
  color: #2ecc71;
}
.custom-marker.red { 
  background-color: #e74c3c; 
  color: #e74c3c;
}
.custom-marker.green.circle,
.custom-marker.green.square {
    border-color: #239054;
}
.custom-marker.red.circle,
.custom-marker.red.square {
    border-color: #b83a2d;
}

/* Legend styles */
.leaflet-control.legend { background: rgba(255, 255, 255, 0.95); padding: 0; border-radius: 8px; box-shadow: 0 1px 5px rgba(0,0,0,0.4); color: #333; max-width: 280px; }
.legend-header { display: flex; justify-content: space-between; align-items: center; padding: 8px 10px; }
.legend h4 { margin: 0; font-size: 1.1em; }
.legend-toggle { background: #f0f0f0; border: 1px solid #ccc; border-radius: 4px; cursor: pointer; width: 20px; height: 20px; display: flex; justify-content: center; align-items: center; padding: 0; }
.legend-toggle:hover { background-color: #e9e9e9; }
.toggle-icon { font-weight: 700; font-size: 14px; line-height: 1; color: #555; }
.legend-content { padding: 0 10px 10px; max-height: 400px; overflow: hidden; transition: max-height 0.3s ease-out, padding 0.3s ease-out; }
.legend .legend-section { margin-bottom: 8px; }
.legend .legend-section:last-child { margin-bottom: 0; }
.legend strong { display: block; font-size: 1em; margin-bottom: 5px; padding-bottom: 2px; border-bottom: 1px solid #ddd; }
.legend .legend-item { display: flex; align-items: center; margin-bottom: 5px; }
.legend .legend-shape { width: 18px; height: 18px; margin-right: 8px; flex-shrink: 0; }
.legend .legend-shape.circle { background: #555; border-radius: 50%; }
.legend .legend-shape.square { background: #555; }
.legend .legend-shape.triangle { background: transparent; width: 0; height: 0; border-left: 9px solid transparent; border-right: 9px solid transparent; border-bottom: 18px solid #555; }
.legend .legend-color-box { width: 18px; height: 18px; margin-right: 8px; flex-shrink: 0; border: 1px solid #777; }
.legend .legend-cluster-example { display: flex; align-items: center; margin-top: 5px; }
.legend .legend-cluster-example svg { margin-right: 10px; flex-shrink: 0; }
.legend .legend-cluster-desc { font-size: 0.9em; line-height: 1.3; }
.legend .legend-cluster-desc strong { border: none; display: inline; font-weight: bold; padding: 0; margin: 0; }
.legend.minimized .legend-content { max-height: 0; padding-top: 0; padding-bottom: 0; overflow: hidden; }
</style>